import React, {
  FC, useEffect,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import { useCookies } from 'react-cookie';
import './Styles.css';
import { parseUrlParams, getIsEnglish } from '../../../lib/utils/utils';
import Home from '../Home';
import {  getFoodifoxDataLazy, updateFoodifoxData } from '../../../lib/axios/FoodifoxDataAPI';
import { Box, Cab } from '../../../lib/types';
import Loading from '../../../components/Loading';
import TermsConditionsModal from '../Common/TermsConditionsModal';
import DriverLink from './DriverLink';
import UserLink from './UserLink';
import { getUserId } from '../../../lib/Authentication';
import AiModal from '../Common/AiModal';
import Support from '../Support';
import TempFieldUtils from '../../../lib/TempFieldUtils';

const Main: FC<Props> = ({ setVersion, cabId32Str, setCabId32Str }) => {
  const params = parseUrlParams<{ cabId: string; firebaseId: string }>(window.location.href);
  // 显示AI
  const [showAi, setShowAi] = useState<boolean>(false);
  // AI对话验证的手机号，每次进入都需要验证
  const [aiPhoneNumber, setAiPhoneNumber] = useState<string>();
  // 操作角色，driver或recipient
  const [operationRole, setOperationRole] = useState<string>();
  // 访问的页面
  const [targetPage, setTargetPage] = useState<string>('home');
  const [isEnglish, setIsEnglish] = useState<boolean>(true);
  const [showTermsConditionsModal, setShowTermsConditionsModal] = useState<boolean>(false);
  const [showAgreeTermsBtn, setShowAgreeTermsBtn] = useState<boolean>(false);
  const [isAgreeTerms, setIsAgreeTerms] = useState<boolean>();
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [driverId, setDriverId] = useState<number>();
  const [userId, setUserId] = useState<number>();
  const [orderId, setOrderId] = useState<number>();
  const [phoneNumber, setPhoneNumber] = useState<number>();
  // 取餐码
  const [pickupCode, setPickupCode] = useState<string>();
  // 房间号
  const [pickupRoomNo, setPickupRoomNo] = useState<string>();
  // 当前选择的平台
  const [selectedPlatform, setSelectedPlatform] = useState<number>();
  // 已打开的柜门
  const [openedBoxes, setOpenedBoxes] = useState<Box[]>([]);
  // 大小柜组合时，小柜柜子ID和大柜柜子ID
  const [smallCabId, setSmallCabId] = useState<number>();
  const [largeCabId, setLargeCabId] = useState<number>();
  // 柜子信息
  const [cabData, setCabData] = useState<Cab>();

  const [cookies, setCookie] = useCookies(['fox_h5_language']);
  const [deviceCode, setDeviceCode] = useState<string>(localStorage.getItem("device_code") || '');

  // 更新客户端中英文
  const [updateUserClientLang] = updateFoodifoxData('/foxH5Client/updateUserClientLang');
  const [updateDriverClientLang] = updateFoodifoxData('/foxH5Client/updateDriverClientLang');

  /**
   * 初始化页面是显示中文还是显示英文
   */
  useEffect(() => {
    // 设置是否是显示英文
    const lang = getIsEnglish(cookies, setCookie);
    TempFieldUtils.currentIsEnglish = lang;
    setIsEnglish(lang);
  }, []);

  /**
   * 初始化设备ID
   */
  useEffect(() => {
    if (!deviceCode) {
      const code = uuid() + '_h5_uuid';
      localStorage.setItem('device_code', uuid() + '_h5_uuid');
      setDeviceCode(code);
    }
  }, [deviceCode]);

  /**
   * 更新用户客户端中英文
   */
  useEffect(() => {
    if (userId) {
      updateUserClientLang({
        param: {
          userId,
          clientLang: isEnglish ? 'EN' : 'ZH',
        },
      });
    }
  }, [userId, isEnglish]);

  /**
   * 更新司机客户端中英文
   */
  useEffect(() => {
    if (driverId) {
      updateDriverClientLang({
        param: {
          driverId,
          clientLang: isEnglish ? 'EN' : 'ZH',
        },
      });
    }
  }, [driverId, isEnglish]);

  useEffect(() => {
    if (targetPage === 'home') {
      setOperationRole(undefined);
      // 进入首页后，清空之前选择的平台
      setSelectedPlatform(undefined);
      setOpenedBoxes([]);
    }
  }, [targetPage]);

  return (
    <>
      {
        showTermsConditionsModal && (
          <TermsConditionsModal
            callback={(isAgree) => {
              setIsAgreeTerms(isAgree);
              setShowTermsConditionsModal(false);
            }}
            showAgreeBtn={showAgreeTermsBtn}
            showExitBtn={showAgreeTermsBtn}
            isEnglish={isEnglish}
          />
        )
      }
      {
        // AI 弹窗，需要验证手机号
        showAi && cabData && deviceCode && aiPhoneNumber && (
          <AiModal
            setShowModal={setShowAi}
            setTargetPage={setTargetPage}
            isEnglish={isEnglish}
            setIsEnglish={setIsEnglish}
            cabId={cabData.cabId}
            deviceCode={deviceCode}
            enterPhoneNumber={aiPhoneNumber}
          />
        )
      }
      {
        (cabId32Str && (!cabData || showLoading)) && (
          <Loading />
        )
      }
      <div className="main-div">
        {/* 首页 */}
        {
          targetPage === 'home' && deviceCode && (
            <Home
              cabId32Str={cabId32Str}
              deviceCode={deviceCode}
              cab={cabData}
              ckFirebaseId={params.firebaseId}
              smallCabId={smallCabId}
              largeCabId={largeCabId}
              setShowLoading={setShowLoading}
              setSmallCabId={setSmallCabId}
              setLargeCabId={setLargeCabId}
              setCabData={setCabData}
              setCabId32Str={setCabId32Str}
              setVersion={setVersion}
            />
          )
        }
        {/* 客服支持页面 */}
        {
          targetPage === 'support' && cabData && (
            <Support
              cab={cabData}
              isEnglish={isEnglish}
              setIsEnglish={setIsEnglish}
              setShowAi={setShowAi}
              setTargetPage={setTargetPage}
              // 进入AI对话的用户手机号
              setAiPhoneNumber={setAiPhoneNumber}
            />
          )
        }
        {
          (cabData && operationRole === 'driver' && deviceCode) && (
            <DriverLink
              targetPage={targetPage}
              cabId32Str={params.cabId}
              deviceCode={deviceCode}
              isEnglish={isEnglish}
              cab={cabData}
              driverId={driverId}
              selectedPlatform={selectedPlatform}
              pickupRoomNo={pickupRoomNo}
              pickupCode={pickupCode}
              orderId={orderId}
              setTargetPage={setTargetPage}
              setIsEnglish={setIsEnglish}
              setShowLoading={setShowLoading}
              setDriverId={setDriverId}
              setPickupCode={setPickupCode}
              setSelectedPlatform={setSelectedPlatform}
              setPickupRoomNo={setPickupRoomNo}
              setOrderId={setOrderId}
              ckFirebaseId={params.firebaseId}
              smallCabId={smallCabId}
              largeCabId={largeCabId}
              setOpenedBoxes={setOpenedBoxes}
              openedBoxes={openedBoxes}
            />
          )
        }

        {
          (cabData && operationRole === 'recipient') && (
            <UserLink
              targetPage={targetPage}
              cabId32Str={params.cabId}
              isEnglish={isEnglish}
              cab={cabData}
              userId={userId}
              phoneNumber={phoneNumber}
              orderId={orderId}
              pickupCode={pickupCode}
              selectedPlatform={selectedPlatform}
              pickupRoomNo={pickupRoomNo}
              setTargetPage={setTargetPage}
              setIsEnglish={setIsEnglish}
              setShowLoading={setShowLoading}
              setPickupCode={setPickupCode}
              setOrderId={setOrderId}
              setUserId={setUserId}
              setPhoneNumber={setPhoneNumber}
              setPickupRoomNo={setPickupRoomNo}
              setSelectedPlatform={setSelectedPlatform}
              setOpenedBoxes={setOpenedBoxes}
              openedBoxes={openedBoxes}
            />
          )
        }
      </div>
    </>
  );
};

export interface Props {
  cabId32Str: string;
  setCabId32Str: (cabId32Str: string) => void;
  setVersion: (version?: string) => void;
}

export default Main;
